import { useMemo } from 'react';

import {
  ageFromBucket,
  sessionEnergyReadout,
  type EffortScale,
  type WorkoutEnergyReadout,
} from '@jackedlogs/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import type { LoggerProfile } from './hostDeps';
import type { EditingTarget } from './LoggerScreen';
import type { LoggerMode } from './loggerMode';
import { useLiveWorkoutEnergy } from './useLiveWorkoutEnergy';
import { useHcWinsCalories } from './state/hcCaloriesPreference';

// HC-8 — the workout energy readout for the logger footer + the post-save card.
// Extracted from useLoggerScreen (500-line cap), the same "compose small hooks"
// shape that file uses. Two sources, resolved at READ:
//   • editing a SAVED + ENRICHED session (mode!=='repeat', carries measured
//     calories/HR from HC) → resolve from the STORED fields so the athlete sees
//     the MEASURED value + effort, not a live re-estimate (B2c). This is what
//     gives the "HC wins" toggle teeth on mobile.
//   • otherwise (live logging, a repeat clone, or a plain saved session) → the
//     live estimate from the draft; there's no measured HR live, so effort stays
//     absent (hrMaxPct null).
// The hcWins preference is read ONCE here and threaded into the live estimator
// so the two paths don't each subscribe to it independently.
export function useWorkoutEnergyReadout(
  draft: LoggerDraft,
  effortScale: EffortScale,
  profile: LoggerProfile,
  editing: EditingTarget | null,
  mode: LoggerMode,
): WorkoutEnergyReadout | null {
  const hcWins = useHcWinsCalories().enabled;
  const live = useLiveWorkoutEnergy(draft, effortScale, profile, hcWins);
  return useMemo<WorkoutEnergyReadout | null>(() => {
    const w = editing?.workout;
    const enriched =
      w !== undefined &&
      mode === 'log-past' &&
      (w.calories !== undefined ||
        w.avgHrBpm !== undefined ||
        w.peakHrBpm !== undefined);
    if (enriched) {
      return sessionEnergyReadout(w, {
        hcWins,
        ageYears: ageFromBucket(profile.ageBucket),
      });
    }
    return live
      ? {
          resolved: live.resolved,
          hrMaxPct: null,
          // LG-46: the live count counts the set not yet done — flag it so the
          // (i) provenance can declare the projection. Only here (the live
          // path); the enriched/saved branch above is a measurement. ⛔ The
          // POST-SAVE summary reuses this same bundle (the draft isn't reset
          // until Done), so that card forces projected=false — nothing is
          // "about to do" on a saved session.
          projected: live.projected,
        }
      : null;
  }, [editing?.workout, mode, hcWins, profile.ageBucket, live]);
}
